import React, { useState, useEffect, useRef, useCallback } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import Button from "../Tools/Components/Button";
import Creditor from "./Components/Creditor";
import { useTranslation } from 'react-i18next';

const FactuurKvkPagina = () => {
    const axiosClient = useRef(null);
    const [pageNr, setPageNr] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [creditors, setCreditors] = useState([]);
    const [searchString, setSearchString] = useState("");
    const { t } = useTranslation();

    const fetchCreditors = useCallback(() => {
        if (!axiosClient.current) return;

        axiosClient.current.get(`${process.env.REACT_APP_INKOFACT_API_URL}Klant/?entries=${entriesPerPage}&page=${pageNr}&searchString=${searchString}`)
            .then(response => {
                setCreditors(response.data.collection);
                setHasNextPage(response.data.hasNextPage);
            })
            .catch(() => {
                setCreditors([]);
            });
    }, [entriesPerPage, pageNr, searchString]);

    useEffect(() => {
        const httpService = HttpService;
        httpService.configure();
        axiosClient.current = httpService.getAxiosClient();
        fetchCreditors();
    }, [fetchCreditors]);

    const cyclePage = (nrToCycle = 0) => {
        setPageNr(prevPageNr => Math.max(prevPageNr + nrToCycle, 1));
    };

    const handleSearchChange = (searchString) => {
        setSearchString(searchString);
        setPageNr(1);
    };

    const handleEntriesChange = (entries) => {
        setEntriesPerPage(entries);
    };

    const renderPageButton = (text, nrToCycle) => (
        <div className="w-[42px] h-[23px] relative" data-name="Page button">
            <button 
                className="bg-[#dedede] dark:bg-[#3F3F46] border-2 border-black rounded-full w-full h-full flex items-center justify-center" 
                onClick={() => cyclePage(nrToCycle)}
            >
                <span className="text-sm font-medium">{text}</span>
            </button>
        </div>
    );

    return (
        <div className="relative size-full" data-name="Content Box">
            <div className="flex flex-row items-start max-w-inherit min-w-inherit size-full gap-5">
                {/* Tools Content Box */}
                <div className="w-100 py-2">
                    <div className="dark:bg-[rgba(55,65,81,0.75)] content-fill flex flex-col items-center relative size-full">
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 border-2 border-[#b0b6c1] pointer-events-none"
                        />
                        <div className="flex flex-col w-[364px]">
                            <div className="h-[80px] bg-[#00aaad] dark:bg-[#008b8e] border-2 border-[#b0b6c1] flex items-center justify-center">
                                <div className="capitalize font-bold text-[16px] tracking-[-0.08px]">
                                    {t("inkofactToolsBox.header")}
                                </div>
                            </div>
                            <div className="bg-[rgba(243,244,246,0.75)] border-2 border-t-0 border-[#b0b6c1] flex flex-col items-center gap-4 p-6">
                                <Button text={t("inkofactToolsBox.promptBtn")} onClick="/PromptOpt" />
                                <Button text={t("inkofactToolsBox.referentieBtn")} onClick="/FactuurReferentie"/>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="box-border content-stretch flex flex-col items-center max-w-inherit min-w-inherit px-0 py-2 relative size-full">
                    
                    {/* Content Box Header */}
                    <div className="bg-[#00aaad] dark:bg-[#008b8e] relative shrink-0 w-full" data-name="ContentBoxHeader">
                        <div aria-hidden="true" className="absolute border border-gray-400 border-solid inset-0 pointer-events-none" />
                        <div className="flex flex-col items-center justify-center size-full">
                            <div className="box-border content-stretch flex flex-col items-center justify-center px-[530px] py-[10px] relative w-full">
                                <div className="capitalize flex flex-col font-['Inter:Bold',sans-serif] font-bold justify-center leading-[0] not-italic relative shrink-0 text-[20px] text-center tracking-[-0.1px] w-[200px] h-[59px]">
                                    <p className="leading-[1.45]">{t("kvks.header")}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Frame */}
                    <div className="bg-[rgba(243,244,246,0.75)] dark:bg-[rgba(55,65,81,0.75)] relative shrink-0 w-full" data-name="MainContentFrame">
                        <div aria-hidden="true" className="absolute border border-gray-400 border-solid inset-0 pointer-events-none" />
                        <div className="flex flex-col items-center size-full">
                            <div className="box-border content-stretch flex flex-col gap-[10px] items-center px-[28px] py-[10px] relative w-full">
                                
                                {/* Search Bar and Button Frame */}
                                <div className="content-stretch flex flex-col h-[23px] items-center justify-between relative shrink-0 w-full" data-name="Search Bar and button">
                                    <div className="absolute bottom-0 left-0 top-0">
                                        <div className="contents pointer-events-auto sticky top-0" data-name="Search Bar">
                                            <div className="absolute capitalize flex flex-col font-['Inter:Bold',sans-serif] font-bold justify-center leading-[0] left-[30px] not-italic text-[16px] text-center text-nowrap top-[11.5px] tracking-[-0.08px] translate-x-[-50%] translate-y-[-50%]">
                                                <p className="whitespace-pre">{t("kvks.searchLabel")}:</p>
                                            </div>
                                            <div className="absolute bg-white dark:bg-gray-700 h-[23px] left-[80px] top-0 w-[600px]">
                                                <input 
                                                    className="dark:bg-gray-700 w-full h-full px-2 leading-[1.45]" 
                                                    onChange={e => handleSearchChange(e.target.value)} 
                                                    type="text" 
                                                    placeholder={t("kvks.searchPlaceholder")} 
                                                />
                                                <div aria-hidden="true" className="absolute border border-black border-solid inset-0 pointer-events-none" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute bottom-0 right-0 top-0">
                                        <Button text={t("kvks.newBtn")} onClick={"/FactuurKvk/create"} height="23" width="290" />
                                    </div>
                                </div>

                                {/* Table */}
                                <div className="content-stretch flex flex-col items-center justify-center relative shrink-0 w-full" style={{ textAlign: "left" }} data-name="Table">
                                    <div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(58,173,255,0.5)] h-[35px] relative shrink-0 w-full" data-name="TableHeader">
                                        <div aria-hidden="true" className="absolute border border-black border-solid inset-0 pointer-events-none" />
                                        <div className="flex flex-row items-center size-full">
                                            <div className="box-border capitalize content-stretch flex font-['Inter:Bold',sans-serif] font-bold h-[35px] items-center leading-[0] not-italic px-[10px] py-[6px] relative text-[16px] tracking-[-0.08px] w-full">
                                                <div className="basis-0 flex flex-col grow h-full justify-center min-h-px min-w-px relative shrink-0">
                                                    <p className="leading-[1.45]">{t("kvks.sender")}</p>
                                                </div>
                                                <div className="basis-0 flex flex-col grow h-full justify-center min-h-px min-w-px relative shrink-0">
                                                    <p className="leading-[1.45]">{t("kvks.alias")}</p>
                                                </div>
                                                <div className="basis-0 flex flex-col grow h-full justify-center min-h-px min-w-px relative shrink-0">
                                                    <p className="leading-[1.45]">KVK</p>
                                                </div>
                                                <div className="basis-0 flex flex-col grow h-full justify-center min-h-px min-w-px relative shrink-0">
                                                    <p className="leading-[1.45]">{t("kvks.nrOfOpts")}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {creditors.map((creditor, index) => (
                                        <Creditor key={creditor.id} creditor={creditor} index={index} />
                                    ))}
                                </div>

                                {/* Page Cycle Frame */}
                                {searchString === "" && (
                                    <div className="flex items-center gap-[10px]">
                                        <span className="text-sm font-medium">{t("kvks.pageSizeLabel")}:</span>
                                        <div className="w-[56px] h-[23px] relative" data-name="Page entries box">
                                            <div className="bg-white dark:bg-gray-700 border border-black rounded-sm w-full h-full flex items-center justify-center">
                                                <input
                                                    id="entriesPerPage"
                                                    type="number"
                                                    onChange={e => handleEntriesChange(e.target.value)}
                                                    className="dark:bg-gray-700 w-[calc(100%-4px)] h-[calc(100%-4px)] text-center text-sm leading-[1.45] outline-none"
                                                    value={entriesPerPage}
                                                />
                                            </div>
                                        </div>
                                        {pageNr > 1 && renderPageButton('<<', -1)}
                                        {hasNextPage && renderPageButton('>>', 1)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FactuurKvkPagina;
